import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger, NavigationMenuContent, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
const useSmoothScroll = () => {
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
};
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="36337" className="hidden lg:flex items-center gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-20 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent text-secondary uppercase text-[14px] font-semibold tracking-wider hover:text-primary data-[state=open]:text-primary transition-colors">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-background border border-border">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-sm p-3 leading-none no-underline outline-none transition-colors hover:bg-muted hover:text-primary focus:bg-muted focus:text-primary">
                            <div className="text-sm font-bold leading-none text-secondary uppercase tracking-tight">{child.label}</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground mt-1">
                              Read our full investigative report on {child.label.toLowerCase()}.
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent text-secondary uppercase text-[14px] font-semibold tracking-wider hover:text-primary hover:bg-transparent transition-colors")}>
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/98 backdrop-blur-xl border-b border-border p-6 flex flex-col gap-4 shadow-2xl lg:hidden animate-in slide-in-from-top-5 z-40">
      <nav className="flex flex-col gap-2">
        {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col">
            <Link to={item.href || "#"} className="text-lg font-bold py-2 border-b border-border/30 text-secondary uppercase tracking-wider hover:text-primary transition-colors" onClick={onClose}>
              {item.label}
            </Link>
            {item.children && <div className="pl-4 flex flex-col gap-1 mt-1">
                {item.children.map((child, j) => <Link key={child.id} data-index={j} to={child.href || "#"} className="text-sm text-muted-foreground py-2 hover:text-primary block font-medium" onClick={onClose}>
                    {child.label}
                  </Link>)}
              </div>}
          </div>)}
      </nav>
      <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-border/30">
        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider" asChild onClick={onClose}>
          <Link to="#">Submit a Tip</Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useSmoothScroll();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b overflow-hidden", isScrolled ? "bg-background/80 backdrop-blur-[12px] border-border shadow-sm" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 h-auto py-2 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 max-h-12">
          <Link to="/HomePage" className="flex items-center gap-2">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605323/img/image_dpauog5z.png" alt="Assessment Watch" className="h-12 max-h-12 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="37">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          
          <div className="hidden lg:block">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-bold uppercase tracking-widest px-6">
              <Link to="#">Submit a Tip</Link>
            </Button>
          </div>

          {}
          <Button variant="ghost" size="icon" className="lg:hidden text-secondary hover:text-primary transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {}
      <MenuProvider menu_id="37">
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}